import { Box, Button, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SchoolIcon from '@mui/icons-material/School'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

interface MemorizationEmptyStateProps {
  /** Path the primary CTA navigates to. Defaults to /reader. */
  primaryCtaPath?: string
  /** Optional callback to re-show the full onboarding (e.g. "Show guide again"). */
  onShowGuide?: () => void
}

/**
 * Compact empty-state shown when the user has previously completed or
 * dismissed the memorization onboarding but has zero verses (e.g. they added
 * some, then deleted them all). Less visual weight than MemorizationOnboarding
 * — a calm prompt rather than a step-by-step guide.
 */
export function MemorizationEmptyState({
  primaryCtaPath = '/reader',
  onShowGuide,
}: MemorizationEmptyStateProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const safePath = primaryCtaPath.startsWith('/') && !primaryCtaPath.startsWith('//')
    ? primaryCtaPath
    : '/reader'

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        py: { xs: 4, sm: 6 },
        px: 2,
      }}
    >
      <SchoolIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.6, mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t('emptyState.memorization.title')}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 420, mb: 3, overflowWrap: 'anywhere' }}
      >
        {t('emptyState.memorization.subtitle')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: 'center' }}>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(safePath)}
        >
          {t('emptyState.memorization.primaryCta')}
        </Button>
        {onShowGuide && (
          <Button size="small" onClick={onShowGuide} sx={{ color: 'text.secondary' }}>
            {t('emptyState.memorization.showGuide')}
          </Button>
        )}
      </Stack>
    </Box>
  )
}
